import { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormValues } from './useFormInitialisation';

// Optional string fields that the form shows/hides behind a ToggleBtn.
type ToggleableFieldName = 'title' | 'description' | 'source';

interface Props {
  fieldName: ToggleableFieldName;
  methods: UseFormReturn<FormValues>;
  // Whether the currently selected memo type supports this field.
  canShow: boolean;
  // Whether the memo being edited already has a value for this field.
  hasInitialValue: boolean;
}

// Shared machinery for an optional, toggleable text field. Keeps the field
// registered while visible and clears/unregisters it when hidden, so hidden
// fields never contribute a value on submit. Used by useTitle, useDescription
// and useSource, which layer their type-specific `canShow` rules on top.
export const useToggleableField = ({
  fieldName,
  methods,
  canShow,
  hasInitialValue,
}: Props) => {
  const { register, unregister, setValue, getValues } = methods;

  // A restored composer draft (or any other pre-seeded default) also counts
  // as "has content" — not just an edited memo's original value — so the
  // chip reveals its field on mount instead of hiding an already-filled one.
  const defaultVisible = canShow && (hasInitialValue || !!getValues(fieldName));

  const [isVisible, setIsVisible] = useState(defaultVisible);

  useEffect(() => {
    setIsVisible(defaultVisible);
  }, [defaultVisible]);

  const toggle = () => {
    if (isVisible) {
      unregister(fieldName);
      setValue(fieldName, '');
    } else {
      register(fieldName);
      if (!getValues(fieldName)) {
        setValue(fieldName, '');
      }
    }
    setIsVisible((prev) => !prev);
  };

  return { isVisible, toggle };
};
